import React, { useEffect, useRef, useState } from 'react';
import { colors, typography } from '../../../styles/theme';
import { FILTER_DROPDOWN_HEIGHT, FILTER_ACCENT } from './FilterPanel.styles';

const DEBOUNCE_MS = 300;

const wrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
};

const labelStyle = (isActive) => ({
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  color: colors.textPanelDescription,
  opacity: isActive ? 1 : 0.75,
});

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
};

const fieldStyle = ({ isActive = false, disabled = false } = {}) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  height: `${FILTER_DROPDOWN_HEIGHT}px`,
  boxSizing: 'border-box',
  padding: '0 12px',
  borderRadius: '6px',
  border: `1px solid ${isActive ? FILTER_ACCENT : colors.border}`,
  backgroundColor: colors.backgroundSurface,
  opacity: disabled ? 0.7 : 1,
});

const inputStyle = (disabled) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: disabled ? 'rgba(255,255,255,0.35)' : colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  cursor: disabled ? 'not-allowed' : 'text',
});

const hintStyle = {
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
  lineHeight: 1.4,
  opacity: 0.75,
};

function toDraft(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
}

function parseBound(text) {
  const trimmed = String(text || '').trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function rangeOrNull(min, max) {
  if (min == null && max == null) return null;
  const next = {};
  if (min != null) next.min = min;
  if (max != null) next.max = max;
  return next;
}

/**
 * Rango libre `{ min?, max? }` (precio CAD v1). Debounce al emitir.
 */
export default function FilterRangeInput({
  label,
  value = null,
  disabled = false,
  hint = null,
  onChange,
}) {
  const [minDraft, setMinDraft] = useState(() => toDraft(value?.min));
  const [maxDraft, setMaxDraft] = useState(() => toDraft(value?.max));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    const applied = value && (value.min != null || value.max != null) ? value : null;
    const last = lastEmittedRef.current;
    const same = (applied?.min ?? null) === (last?.min ?? null)
      && (applied?.max ?? null) === (last?.max ?? null);
    if (same) return;
    lastEmittedRef.current = applied;
    setMinDraft(toDraft(applied?.min));
    setMaxDraft(toDraft(applied?.max));
  }, [value]);

  useEffect(() => {
    const min = parseBound(minDraft);
    const max = parseBound(maxDraft);
    const next = rangeOrNull(min, max);
    const last = lastEmittedRef.current;
    const same = (next?.min ?? null) === (last?.min ?? null)
      && (next?.max ?? null) === (last?.max ?? null);
    if (same) return undefined;
    const timer = setTimeout(() => {
      lastEmittedRef.current = next;
      onChangeRef.current?.(next);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [minDraft, maxDraft]);

  const isActive = value != null && (value.min != null || value.max != null);

  return (
    <div style={wrapStyle}>
      <span style={labelStyle(isActive)}>{label}</span>
      <div style={rowStyle}>
        <div style={fieldStyle({ isActive: value?.min != null, disabled })}>
          <input
            type="text"
            inputMode="decimal"
            value={minDraft}
            placeholder="Mín."
            disabled={disabled}
            aria-label={`${label} mínimo`}
            style={inputStyle(disabled)}
            onChange={(event) => setMinDraft(event.target.value)}
          />
        </div>
        <div style={fieldStyle({ isActive: value?.max != null, disabled })}>
          <input
            type="text"
            inputMode="decimal"
            value={maxDraft}
            placeholder="Máx."
            disabled={disabled}
            aria-label={`${label} máximo`}
            style={inputStyle(disabled)}
            onChange={(event) => setMaxDraft(event.target.value)}
          />
        </div>
      </div>
      {hint ? <span style={hintStyle}>{hint}</span> : null}
    </div>
  );
}
